import { useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";

export const usePersonPhotoUpload = () => {
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadPersonPhoto = useCallback(
    async (file: File, estateId: number): Promise<string | null> => {
      try {
        setIsUploading(true);
        setError(null);

        // Get access token for authorization
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const accessToken = session?.access_token;

        if (!accessToken) {
          throw new Error("No access token available");
        }

        // Step 1: Get presigned URL
        const presignedResponse = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/media/profile-picture/presigned-url`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
              apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({
              fileName: file.name,
              estateId,
            }),
          },
        );

        if (!presignedResponse.ok) {
          throw new Error("Failed to get presigned URL");
        }

        const { uploadUrl, fileKey } = await presignedResponse.json();

        // Step 2: Upload file to S3
        const uploadResponse = await fetch(uploadUrl, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
          },
        });

        if (!uploadResponse.ok) {
          throw new Error("Failed to upload file to S3");
        }

        // Step 3: Save the file key to the person's main_image_url
        const { error: updateError } = await supabase
          .from("estates")
          .update({ main_image_url: fileKey })
          .eq("estate_id", estateId);

        if (updateError) {
          throw new Error("Failed to save photo metadata");
        }

        queryClient.invalidateQueries({ queryKey: ["person-photo-url", estateId, undefined] });

        return fileKey;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Upload failed";
        setError(errorMessage);
        console.error("Error uploading person photo:", err);
        return null;
      } finally {
        setIsUploading(false);
      }
    },
    [],
  );

  return {
    uploadPersonPhoto,
    isUploading,
    error,
  };
};
